import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import {
  SkinTypeDefStorage,
  SKIN_TYPE_DEF_SCHEMA_VERSION,
  toServiceSkinTypeDef,
} from '../storage/SkinTypeDefStorage';
import { ensureBundledSkinAssets } from '../services/skin/resolveSkinChannelInfo';
import type { SkinTypeDefDto } from '../types/api/service';

export interface AccountSkinDefResult {
  /** null while loading, on failure, or when no skinType is known yet. */
  skinDef: SkinTypeDefDto | null;
  /**
   * True once a real lookup has finished (including a failed one, with skinDef left null) — a
   * consumer that hydrates skin-dependent draft state exactly once (see AccountSettingsView /
   * AssessmentOptionsView) should gate that hydration on this, not just on skinDef being
   * non-null, so it never captures an empty-skin computation as if it were final.
   */
  ready: boolean;
}

const NOT_READY: AccountSkinDefResult = { skinDef: null, ready: false };

/**
 * Resolves the account's SkinTypeDef from the bundled content already synced locally (see
 * SkinTypeDefStorage) — converted to the same PascalCase shape AccountSettingsDto.SkinDef used
 * to carry (types/api/service.ts's SkinTypeDefDto) — instead of the per-sign-in backend fetch
 * that field required. A skin missing locally, or stored under an older schema (see
 * SKIN_TYPE_DEF_SCHEMA_VERSION), is topped up from the web content bundle via
 * ensureBundledSkinAssets (web only — native's bundle is already loaded by app-start's
 * useBundledContent, and ensureBundledSkinAssets' `fetch` of the public asset has nothing to
 * target there).
 */
export function useAccountSkinDef(
  storage: any,
  skinType: string | null | undefined,
  languageTypeId = '1'
): AccountSkinDefResult {
  const [result, setResult] = useState<AccountSkinDefResult>(NOT_READY);
  // Guards against a slow lookup for a PREVIOUS skinType/account clobbering a newer one that
  // resolved first (e.g. switching accounts to a different skin in quick succession).
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!storage || !skinType) {
      setResult(NOT_READY);
      return;
    }

    const requestId = ++requestIdRef.current;
    setResult(NOT_READY);

    void (async () => {
      const skinDefStorage = new SkinTypeDefStorage(storage);
      let local = await skinDefStorage.getSkinTypeDef(skinType, languageTypeId);
      const isStale = !local || (local.schemaVersion ?? 0) < SKIN_TYPE_DEF_SCHEMA_VERSION;

      if (isStale && Platform.OS === 'web') {
        try {
          await ensureBundledSkinAssets(storage);
          local = await skinDefStorage.getSkinTypeDef(skinType, languageTypeId);
        } catch (error) {
          console.warn('useAccountSkinDef: failed to load bundled skin assets', error);
        }
      }

      if (requestIdRef.current === requestId) {
        setResult({ skinDef: local ? toServiceSkinTypeDef(local) : null, ready: true });
      }
    })();
  }, [storage, skinType, languageTypeId]);

  return result;
}
